import {Main} from 'src/Layouts';
import {Drupal, Graphql, YouInvest} from 'src/Api';
import {InvestWithAjBell} from 'src/Components/Molecules/Card';
import {InstrumentMain, InstrumentInfoBoxes} from 'src/Components/Organisms';
import {SideNavigation} from 'src/Components/Molecules/Navigation';
import {getTitle, sendCacheHeaders} from 'src/Utils';
import {PortfolioSection} from './Components/Card';
import * as Sentry from '@sentry/nextjs';

export default function Portfolio({instrumentData}) {

    const getBreadCrumb = () => {
		if (instrumentData) {
			return `${instrumentData.quote.nameShort} (${instrumentData.quote.marketCode}) - Portfolio`;
		}
	}

    return <Main title={getTitle(instrumentData, 'Portfolio')} nav={instrumentData.nav} breadcrumb={getBreadCrumb()} marketCode={instrumentData.quote.marketCode}>
        <div className="ajb-grid-row padding-bottom-20">
            <div className="ajb-col-2 ajb-col-8-breakpoint padding-top-20 padding-bottom-0-sm">
                <SideNavigation  {...instrumentData} />
                <InvestWithAjBell />
            </div>
            <div className="ajb-col-10 ajb-col-8-breakpoint main-content-wrapper white-bg margin-top-20 padding-20 padding-10-sm">
                <InstrumentMain {...instrumentData} />
                <InstrumentInfoBoxes {...instrumentData} />
                <PortfolioSection {...instrumentData} />
            </div>
        </div>
    </Main>
}
  
export async function getServerSideProps(context) {

    try { 
        const {marketCode} = context.query;

        const pageData = await YouInvest.getBasicPageData(marketCode);

        const query = `query pageData(
            $identifier: String!
        ) {
            portfolio(identifier: $identifier) {
                name
                date
                suppressionExpiresDate
                numberOfEquities
                numberOfBonds
                top10Holdings {
                    id
                    isin
                    name
                    currency
                    country
                    weighting
                    marketValue
                    sharesHeld
                    changeinSharesHeld
                    marketValueCurrency
                    holdingType
                    maturityDate
                    changeinHolding
                    sedol
                }
                equityValuation {
                    priceEarnings
                    priceBook
                    priceSales
                    priceCashFlow
                    dividendYieldFactor
                    longProjectedEarningsGrowth
                    historicalEarningsGrowth
                    salesGrowth
                    cashFlowGrowth
                    bookValueGrowth
                    styleBox
                }
                equityValuationCategory {
                    priceEarnings
                    priceBook
                    priceSales
                    priceCashFlow
                    dividendYieldFactor
                    longProjectedEarningsGrowth
                    historicalEarningsGrowth
                    salesGrowth
                    cashFlowGrowth
                    bookValueGrowth
                }
                bondStatistics {
                    styleBox
                    effectiveMaturity
                    averageCreditQuality
                    modifiedDuration
                    averageCreditQualityLabel
                }
                bondStatisticsCategory {
                    effectiveMaturity
                    averageCreditQuality
                    modifiedDuration
                    averageCreditQualityLabel
                }
                asset {
                    id
                    value
                    label
                }
                assetShort {
                    id
                    value
                    label
                }
                assetNet {
                    id
                    value
                    label
                }
                assetCategory {
                    id
                    value
                    label
                }
                sector {
                    id
                    value
                    label
                    superSector
                }
                sectorCategory {
                    id
                    value
                    label
                    superSector
                }
                region {
                    id
                    value
                    label
                    superRegion
                }
                regionCategory {
                    id
                    value
                    label
                    superRegion
                }
                marketCapitalisationCat {
                    notClassified
                    breakdownValues {
                    id
                    value
                    label
                    }
                }
                marketCapitalisation {
                    notClassified
                    breakdownValues {
                        id
                        value
                        label
                    }
                }
                creditQuality {
                    value
                    id
                    label
                    benchmarkValue
                    benchmarkId
                    benchmarkLabel
                }
                fixedIncomeSectors {
                    value
                    type
                    label
                    benchmarkValue
                    benchmarkType
                    benchmarkLabel
                }
                fixedIncomeSectorLevel2 {
                    value
                    type
                    label
                    benchmarkValue
                    benchmarkType
                    benchmarkLabel
                }
                fundSize {
                    currency
                    dayEndDate
                    dayEndValue
                }
                shareClassSize {
                    currency
                    dayEndDate
                    dayEndValue
                }
                maturityDistribution {
                    type
                    value
                    label
                }
                maturityDistributionCategory {
                    type
                    value
                    label
                }
            }
        }`;

        const instrumentData = {...pageData, ...(await Graphql.fetch(query, {
            identifier: marketCode
        }))};

        sendCacheHeaders(context, instrumentData);

        return { props: { instrumentData } }

    } catch (e) {
        Sentry.captureException(e);
        return {
            notFound: true
        }
    }
}


